import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { first, tap } from 'rxjs/operators';

import {
  QuickQuoteRequest,
  ReplaceVehiclePLPCPayload,
  SubmitOnlineBindRequestModel
} from '@amfam/policy/models';
import { VehicleFacade } from '@amfam/policy/vehicle/data-access';
import {
  ANSNotification,
  InternalEmail,
  TemplatedEmail
} from '@amfam/shared/utility/shared-services';

import * as quickQuoteDataActions from './quick-quote-data.actions';
import { QuickQuoteDataState } from './quick-quote-data.reducer';
import { quickQuoteDataQuery } from './quick-quote-data.selectors';

@Injectable({ providedIn: 'root' })
export class QuickQuoteDataFacade {
  constructor(
    private store: Store<QuickQuoteDataState>,
    private vehicleFacade: VehicleFacade
  ) {}

  isLoaded$ = this.store.select(quickQuoteDataQuery.getLoaded);
  isLoading$ = this.store.select(quickQuoteDataQuery.getLoading);
  quickQuote$ = this.store.select(quickQuoteDataQuery.getQuickQuotePremium);
  errorOnExceptionANS$ = this.store.select(quickQuoteDataQuery.errorOnExceptionANS);
  errorOnANS$ = this.store.select(quickQuoteDataQuery.errorOnANS);
  errorOnQuickQuote$ = this.store.select(quickQuoteDataQuery.errorOnQuickQuote);
  workOrderNumber$ = this.store.select(quickQuoteDataQuery.getWorkOrderNumber);
  futurePayments$ = this.store.select(quickQuoteDataQuery.getFuturePayments);

  resetVehicle() {
    this.vehicleFacade.reset();
  }

  resetExceptionANSError() {
    this.store.dispatch(quickQuoteDataActions.resetExceptionANSError());
  }

  resetANSError() {
    this.store.dispatch(quickQuoteDataActions.resetANSError());
  }

  resetQuickQuoteError() {
    this.store.dispatch(quickQuoteDataActions.resetQuickQuoteError());
  }

  getQuickQuote(quickQuoteRequest: QuickQuoteRequest) {
    this.resetQuickQuoteError();
    this.store.dispatch(quickQuoteDataActions.getQuickQuote({ quickQuoteRequest }));
  }

  getQuote(quoteRequestPLPC: ReplaceVehiclePLPCPayload) {
    this.resetQuickQuoteError();
    this.store.dispatch(quickQuoteDataActions.getQuote({ quoteRequest: quoteRequestPLPC }));
  }

  resetQuickQuote() {
    this.store.dispatch(quickQuoteDataActions.resetQuickQuote());
  }

  bindQuote(bindPayload: SubmitOnlineBindRequestModel) {
    this.resetQuickQuoteError();
    this.store.dispatch(quickQuoteDataActions.bindQuickQuote({ payload: bindPayload }));
  }

  bindQuotePLPC(bindPayload: SubmitOnlineBindRequestModel) {
    this.resetQuickQuoteError();
    this.store.dispatch(quickQuoteDataActions.bindQuote({ payload: bindPayload }));
  }

  sendCustomerEmailOnQuickQuoteSuccess(email: TemplatedEmail) {
    this.store.dispatch(quickQuoteDataActions.quickQuoteSendCustomerEmail({ email }));
  }

  sendToAgent(
    agentEmail: InternalEmail,
    customerEmail: TemplatedEmail,
    ansNotification: ANSNotification
  ) {
    if (ansNotification) {
      this.resetANSError();
      this.notifyAgent(ansNotification, quickQuoteDataActions.ansNotification);
    }
    if (agentEmail) this.emailAgent(agentEmail, quickQuoteDataActions.quickQuoteSendToAgentEmail);
    if (customerEmail)
      this.emailCustomer(customerEmail, quickQuoteDataActions.quickQuoteSendToAgentCustomerEmail);
  }

  sendToAgentOnError(
    agentEmail: InternalEmail,
    customerEmail: TemplatedEmail,
    ansNotification: ANSNotification
  ) {
    if (ansNotification) {
      this.resetExceptionANSError();
      this.notifyAgent(ansNotification, quickQuoteDataActions.ansNotificationOnError);
    }
    if (agentEmail) this.emailAgent(agentEmail, quickQuoteDataActions.quickQuoteEmailAgentOnError);
    if (customerEmail)
      this.emailCustomer(customerEmail, quickQuoteDataActions.quickQuoteEmailCustomerOnError);
  }

  sendToAgentOnCancelProcess(
    agentEmail: InternalEmail,
    customerEmail: TemplatedEmail,
    ansNotification: ANSNotification
  ) {
    if (ansNotification)
      this.notifyAgent(ansNotification, quickQuoteDataActions.replaceVehicleANSOnCancelProcess);
    if (agentEmail)
      this.emailAgent(agentEmail, quickQuoteDataActions.replaceVehicleAgentEmailOnCancelProcess);
    if (customerEmail)
      this.emailCustomer(
        customerEmail,
        quickQuoteDataActions.replaceVehicleCustomerEmailOnCancelProcess
      );
  }

  sendCustomerEmailOnSendToAgent(email: TemplatedEmail) {
    this.emailCustomer(email, quickQuoteDataActions.quickQuoteSendToAgentCustomerEmail);
  }

  sendAgentEmail(email: InternalEmail) {
    this.emailAgent(email, quickQuoteDataActions.quickQuoteSendToAgentEmail);
  }

  sendAnsNotification(notification: ANSNotification) {
    this.resetANSError();
    this.notifyAgent(notification, quickQuoteDataActions.ansNotification);
  }

  private emailAgent(email: InternalEmail, action: Function) {
    this.store.dispatch(action({ email }));
  }

  private emailCustomer(email: TemplatedEmail, action: Function) {
    this.store.dispatch(action({ email }));
  }

  private notifyAgent(notification: ANSNotification, action: Function) {
    this.store.dispatch(action({ notification }));
  }

  loadFuturePayments(policyNumber: string) {
    this.workOrderNumber$
      .pipe(
        first(),
        tap(submissionId =>
          this.store.dispatch(
            quickQuoteDataActions.loadFuturePayments({
              policyNumber,
              submissionId
            })
          )
        )
      )
      .subscribe();
  }
}
